import {
  collection,
  doc,
  onSnapshot,
  runTransaction
} from 'firebase/firestore';
import { db } from './firebase';
import {
  stripUndefined,
  betsFromDocData,
  sortBetsNewestFirst,
  insertBet,
  updateBetInList,
  removeBetFromList
} from './betTracker/storeCore';

/**
 * Bet Tracker persistence.
 *
 * INTERIM LOCATION — read this before moving anything: bets are
 * stored as a `trackedBets` array field on the user's own
 * userSettings/{uid} document.
 *
 * Why: the target design is the users/{uid}/bets/{betId}
 * subcollection (that's what the firestore.rules file merged in
 * PR #22 describes), but merging a rules FILE does not deploy it —
 * rules ship separately through Firebase tooling, and that deploy is
 * still pending. The previously deployed rules contain no match for
 * the bets subcollection, so every client read/write there fails
 * with PERMISSION_DENIED (both the Parlay Builder's Save button and
 * the tracker's manual add broke exactly this way at launch).
 * userSettings/{uid}, by contrast, has been owner-readable/writable
 * under the deployed rules all along — it is the store the settings
 * service and favorite teams already write through in production —
 * so tracker writes succeed TODAY with no rules change.
 *
 * MIGRATION (when the PR #22 rules deploy lands): move bets back to
 * users/{uid}/bets. Only the storage-adapter section below changes:
 * each stored item already carries its `id` inline, which becomes
 * the subcollection doc id; the record shape (see
 * services/betTracker/records.js) and this module's public API stay
 * exactly as they are. The pure transforms live in
 * betTracker/storeCore.js and are storage-agnostic.
 *
 * All writes are sanitized with stripUndefined(): the app's
 * Firestore instance is not configured with
 * ignoreUndefinedProperties, and Firestore rejects undefined field
 * values anywhere in a payload.
 */

// ------------------------------------------------------------------
// Storage adapter (interim userSettings field store)
// ------------------------------------------------------------------

const BETS_FIELD = 'trackedBets';

const settingsDoc = (uid) => doc(db, 'userSettings', uid);

/** Client-generated id (id-only doc ref; nothing is written). */
const newBetId = () => doc(collection(db, 'betTrackerIds')).id;

/** Current bets array from a transaction snapshot of the doc. */
function currentBets(snapshot) {
  return snapshot.exists() ? betsFromDocData(snapshot.data(), BETS_FIELD) : [];
}

/** Read-modify-write the bets array inside a transaction. */
async function mutateBets(uid, transform) {
  const ref = settingsDoc(uid);
  await runTransaction(db, async (tx) => {
    const snapshot = await tx.get(ref);
    const next = transform(currentBets(snapshot));
    if (next === null) return; // transform opted out of writing
    if (snapshot.exists()) {
      tx.update(ref, { [BETS_FIELD]: next });
    } else {
      tx.set(ref, { [BETS_FIELD]: next });
    }
  });
}

// ------------------------------------------------------------------
// Public API (unchanged from the subcollection implementation)
// ------------------------------------------------------------------

/** Add a bet record; resolves to the new bet id. */
export async function addBet(uid, record) {
  const id = newBetId();
  const data = stripUndefined({ ...record });
  delete data.id;
  const item = { ...data, id };
  await mutateBets(uid, (bets) => insertBet(bets, item));
  return id;
}

/**
 * Live-subscribe to the user's bets, newest first.
 * onBets receives [{ id, ...record }]. Returns the unsubscribe fn.
 */
export function subscribeBets(uid, onBets, onError) {
  return onSnapshot(
    settingsDoc(uid),
    (snapshot) => {
      const bets = snapshot.exists()
        ? betsFromDocData(snapshot.data(), BETS_FIELD)
        : [];
      onBets(sortBetsNewestFirst(bets).map((bet) => ({ ...bet })));
    },
    onError
  );
}

/** Apply a partial update (grading settlement, manual override). */
export async function updateBet(uid, betId, updates) {
  const clean = stripUndefined({ ...updates });
  await mutateBets(uid, (bets) => {
    const next = updateBetInList(bets, betId, clean);
    return next === bets ? null : next;
  });
}

/** Permanently delete one bet. */
export async function deleteBet(uid, betId) {
  await mutateBets(uid, (bets) => {
    const next = removeBetFromList(bets, betId);
    return next === bets ? null : next;
  });
}

export const betTrackerService = { addBet, subscribeBets, updateBet, deleteBet };

export default betTrackerService;
